// ============================================================
// defenceLedger - what stands between the shot and the body
// (v8.253.0). The DEFENCE LEDGER rail's data: every reaction this
// target is ENTITLED to per attack, its effective value after
// defender custom mods and condition penalties, its exact success
// odds, and what forbids it. Entitlement, not a counter - no
// per-round usage is stored anywhere in state, deliberately (the
// auspex plan's D4), so this module must never pretend to track
// spent reactions.
//
// One availability truth: resolveReactionAvailability, the same
// call the defence chain and the log buttons make.
// ============================================================
import { resolveReactionAvailability } from './reactionAvailability';
import { applyDefenderMods } from './defenceCalc';
import { computeDodgeProbability, computeParryProbability } from './defenceProbability';
import { condType } from './conditions';
import type { ConditionModOptions } from './conditionModifiers';
import { DEFAULT_UNNATURAL_DOS_MODE, type UnnaturalDosMode } from './unnaturalDos';
import type { Target } from '../types';

interface DefenceLedgerEntry {
  key: 'dodge' | 'parry' | 'stepAside' | 'wallOfSteel' | 'stance' | 'counter';
  label: string;
  /** Effective test value, mods folded in. Reaction rows only. */
  value?: number;
  /** Exact success chance [0,1] - analytical, not Monte Carlo. */
  odds?: number;
  /** The parry weapon's name, when known. */
  detail?: string;
  /** 'POWER FIELD' and similar riders. */
  note?: string;
  /** Present = the entitlement exists but something forbids it. */
  blockedBy?: string[];
}

export interface DefenceLedger {
  entries: DefenceLedgerEntry[];
}

export interface DefenceLedgerOptions {
  applyConditionMods?: boolean;
  condOptions?: ConditionModOptions;
  unnaturalDosMode?: UnnaturalDosMode;
}

/** One reaction's entitlement, before its numbers join it. */
interface ReactionFacts {
  key: 'dodge' | 'parry';
  label: string;
  trained: boolean;
  allowed: boolean;
}

/**
 * The reaction rows share this assembly; only the facts differ. Untrained
 * outranks forbidden; a reaction forbidden with no condition named is
 * Frenzy's doing.
 */
function reactionRow(
  f: ReactionFacts,
  value: number,
  oddsOf: (value: number) => number,
  disabledBy: string[],
): DefenceLedgerEntry {
  const reasons = disabledBy.length ? disabledBy : ['FRENZIED'];
  const blockedBy = !f.trained ? ['NOT TRAINED'] : !f.allowed ? reasons : undefined;
  return { key: f.key, label: f.label, value, odds: oddsOf(value), ...(blockedBy ? { blockedBy } : {}) };
}

export function deriveDefenceLedger(target: Target, options: DefenceLedgerOptions = {}): DefenceLedger {
  const { applyConditionMods = false, condOptions = {}, unnaturalDosMode = DEFAULT_UNNATURAL_DOS_MODE } = options;
  const avail = resolveReactionAvailability(target, { applyConditionMods, condOptions });
  const eff = applyDefenderMods(
    target.dodge?.agility ?? 0,
    target.parry?.weaponSkill ?? 0,
    target.defenderCustomMods ?? [],
  );
  const unnatural = (charValue: number, multiplier: number) =>
    unnaturalDosMode !== 'off' ? { charValue, multiplier, mode: unnaturalDosMode } : undefined;

  const dodgeUnnatural = unnatural(target.dodge?.agility ?? 0, target.unnaturalAgility || 1);
  const parryUnnatural = unnatural(target.parry?.weaponSkill ?? 0, target.unnaturalWeaponSkill || 1);
  const dodge = reactionRow(
    { key: 'dodge', label: 'DODGE', trained: !!target.dodge?.canDodge, allowed: avail.canDodge },
    eff.effectiveDodge + avail.dodgeMod,
    (v) => computeDodgeProbability(v, dodgeUnnatural).successChance,
    avail.disabledBy,
  );
  const parry = reactionRow(
    { key: 'parry', label: 'PARRY', trained: !!target.parry?.canParry, allowed: avail.canParry },
    eff.effectiveParry + avail.parryMod,
    (v) => computeParryProbability(v, parryUnnatural).successChance,
    avail.disabledBy,
  );
  if (target.parry?.parryWeaponName) parry.detail = target.parry.parryWeaponName;
  if (target.parry?.hasPowerField) parry.note = 'POWER FIELD';
  return { entries: [dodge, parry, ...entitlementRows(target)] };
}

/** The extra reactions and riders the target is entitled to, as label-only rows. */
function entitlementRows(target: Target): DefenceLedgerEntry[] {
  const extraDodges = target.dodge?.additionalDodges || 0;
  const extraParries = target.parry?.additionalParries || 0;
  const inStance = (target.conditions || []).some((c) => condType(c) === 'Ds');
  const rows: (DefenceLedgerEntry | false)[] = [
    extraDodges > 0 && { key: 'stepAside', label: `+${extraDodges} STEP ASIDE` },
    extraParries > 0 && { key: 'wallOfSteel', label: `+${extraParries} WALL OF STEEL` },
    inStance && { key: 'stance', label: 'STANCE +1 REACTION' },
    !!target.counterAttackAvailable && { key: 'counter', label: 'COUNTER-ATTACK' },
  ];
  return rows.filter((r): r is DefenceLedgerEntry => !!r);
}
